import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ChevronRight, Hourglass, Library } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/hooks/use-locale'

export type ArcChapter = {
    number: number
    title: string
    headline: string
}

interface ArcChaptersProps {
    chapters: ArcChapter[]
    titleId: string
    className?: string
}

const ArcChapters: FC<ArcChaptersProps> = ({ chapters, titleId, className }): ReactNode => {
    const { locale, t } = useLocale()
    const latin = locale === 'en'

    if (chapters.length === 0) {
        return (
            <div className={cn('flex flex-col gap-4 rounded-2xl border border-dashed border-gold/60 bg-card/60 p-5 sm:flex-row sm:gap-5 sm:p-6', className)}>
                <span className='flex size-12 shrink-0 items-center justify-center rounded-full bg-accent text-primary'>
                    <Hourglass className='size-6' aria-hidden='true' />
                </span>

                <div className='min-w-0'>
                    <p className={cn('text-base text-foreground/85 sm:pt-2.5', latin ? 'leading-relaxed' : 'leading-[1.85]')}>
                        {t.storyPending}
                    </p>

                    <Link
                        href='/parva'
                        className='group mt-1 inline-flex min-h-11 items-center gap-2 rounded-md text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                    >
                        {t.browseChapters}
                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none' aria-hidden='true' />
                    </Link>
                </div>
            </div>
        )
    }

    const first = chapters[0]

    return (
        <div className={cn('rounded-2xl border border-border/80 bg-card/80 p-5 shadow-[0_1px_2px_rgb(70_40_10/0.05)] sm:p-6', className)}>
            <h3 id={titleId} className='flex items-center gap-2.5 font-heading text-xl font-semibold text-foreground sm:text-2xl'>
                <Library className='size-5 shrink-0 text-primary' aria-hidden='true' />
                {t.storyChaptersInPart}
            </h3>

            <div aria-hidden='true' className='mt-3 h-px w-20 bg-linear-to-r from-gold to-transparent' />

            <ol role='list' aria-labelledby={titleId} className='mt-5 grid gap-2 sm:grid-cols-2'>
                {chapters.map(chapter => (
                    <li key={chapter.number} className='min-w-0'>
                        <Link
                            href={`/parva/${chapter.number}`}
                            className={cn(
                                'group flex h-full min-h-14 items-center gap-3 rounded-xl border border-border/70 bg-background/70 px-3 py-2',
                                'transition-colors hover:border-gold hover:bg-accent/60 motion-reduce:transition-none',
                                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                            )}
                        >
                            <span className='w-10 shrink-0 text-center font-heading text-2xl font-semibold leading-none text-primary lining-nums'>
                                <span className='sr-only'>{t.parva} </span>
                                {chapter.number}
                            </span>

                            {' '}

                            <span className={cn('line-clamp-2 min-w-0 flex-1 text-sm font-medium text-foreground transition-colors group-hover:text-primary', latin ? 'leading-snug' : 'leading-relaxed')}>
                                {chapter.title}
                            </span>

                            <ChevronRight className='size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary motion-reduce:transition-none' aria-hidden='true' />
                        </Link>
                    </li>
                ))}
            </ol>

            <Link
                href={`/parva/${first.number}`}
                className='group mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
            >
                {t.storyStartReading.replace('{number}', String(first.number))}
                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none' aria-hidden='true' />
            </Link>
        </div>
    )
}

export default ArcChapters
